"use client"

import { useEffect } from 'react'
import { getVersionInfo } from '@/lib/version'
import { useHydrationErrorHandler } from './hydration-error-boundary'

export function VersionManager() {
  // Enable global hydration error handling
  useHydrationErrorHandler()
  
  useEffect(() => {
    // Register service worker for better cache control
    if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
      // Helper function to safely update service worker
      const safeUpdate = async (registration: ServiceWorkerRegistration) => {
        try {
          // Check if registration is still valid before updating
          if (!registration || registration.scope !== self.location.origin + '/') {
            console.warn('Service Worker registration invalid, skipping update');
            return;
          }
          
          // Wrap the update call to catch any synchronous errors
          await Promise.resolve().then(() => registration.update());
        } catch (error) {
          console.warn('Service Worker update failed:', error);
          // Silently handle all ServiceWorker errors - don't attempt recovery
          // as it can cause more instability
        }
      };

      // Function to register service worker
      const registerServiceWorker = () => {
        navigator.serviceWorker.register('/sw.js', {
          // Add update via cache to prevent stale service worker issues
          updateViaCache: 'none'
        }).then((registration) => {
          console.log('Service Worker registered:', registration.scope);
          
          // Check for updates every hour (low frequency since deployments are infrequent)
          const updateInterval = setInterval(() => {
            safeUpdate(registration);
          }, 60 * 60 * 1000);
          
          // Clean up interval on unmount
          return () => clearInterval(updateInterval);
        }).catch((error) => {
          console.error('Service Worker registration failed:', error);
          // Don't throw or propagate the error - just log it
        });
      };

      // Initial registration
      registerServiceWorker();
      
      // Listen for service worker messages
      const messageHandler = (event: MessageEvent) => {
        if (event.data.type === 'NEW_VERSION_AVAILABLE') {
          console.log('New version detected by service worker:', event.data.version);
          // The VersionDisplay component will handle the UI notification
        }
      };
      
      navigator.serviceWorker.addEventListener('message', messageHandler);
      
      // Cleanup on unmount
      return () => {
        navigator.serviceWorker.removeEventListener('message', messageHandler);
      };
    }
    
    // Log version info
    const versionInfo = getVersionInfo();
    console.log('App Version:', versionInfo.version);
    console.log('Build Time:', versionInfo.buildTime);
    console.log('Build ID:', versionInfo.buildId);
  }, []);
  
  return null;
}